import { config } from '@uhg-abyss/web/tools/config';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Link } from '@uhg-abyss/web/ui/Link';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPremiumCareContent } from '../../../common/Constants';
import { LinkWithTracking } from '../../../common/LinkWithTracking/LinkWithTracking';
import { Markdown } from '../../../common/Markdown';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { getLanguage } from '../../../frontends/ProviderSearch/context/Internationalization/helpers';
import {
  AccordionItem,
  StyledMarkdown,
  accordionContent,
  accordionHeader,
  accordionItemImages,
  healthPremiumLinkStyle,
} from './Accordion.style';

type Props = {
  content: any;
  expandAll: boolean;
  closeAll: boolean;
  setExpandAll: (expandAll: boolean) => void;
  setCloseAll: (closeAll: boolean) => void;
  source: string | null;
};

export const FAQAccordion = ({
  content,
  expandAll,
  closeAll,
  setExpandAll,
  setCloseAll,
  source,
}: Props) => {
  const { t } = useTranslation();
  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  const [headerValues, setHeaderValues] = useState<string[]>([]);
  const [singleValue, setSingleValue] = useState<boolean>(false);
  const smartChoice_Image = `${config('CDN_BASE_URL')}/cdn/assets/images/${
    source === 'cex' ? 'smart-choice-faq-app-cex.svg' : 'smart-choice.png'
  }`;
  const locale = getLanguage().code;
  const unitedHealthPremiumData = getPremiumCareContent(locale);
  const navigationURL = unitedHealthPremiumData?.popoverURL ?? '';
  const unitedHealthPremiumLinkText =
    unitedHealthPremiumData?.disclaimerLinkText ?? '';

  useEffect(() => {
    if (closeAll) {
      setHeaderValues([]);
      setCloseAll(false);
    }
    if (expandAll) {
      setHeaderValues(content.map((x) => x.title));
      setExpandAll(false);
    }
  }, [closeAll, expandAll]);

  const onValueChangeSingle = (e) => {
    if (e === 'images') {
      setSingleValue(true);
    } else {
      setSingleValue(false);
    }
  };

  const renderAccordionImages = (section) => {
    const value = section?.content.split('<br><br>');
    const combinedValue = value.join('<br><br>');
    return (
      <Flex>
        <Flex>
          <Markdown>{value[0]}</Markdown>
          {!isUmr && (
            <Accordion
              isCollapsible
              onValueChange={onValueChangeSingle}
              type="single"
            >
              <Accordion.Item css={accordionItemImages} value="images">
                <Accordion.Header>
                  {singleValue
                    ? t('FAQ_PAGE.CLOSE_IMAGES')
                    : t('FAQ_PAGE.EXPAND_IMAGES')}
                </Accordion.Header>
                <Accordion.Content
                  css={{
                    'abyss-accordion-content-text': {
                      width: '50%',
                      padding: '0px !important',
                    },
                  }}
                >
                  <Flex direction="row">
                    <img src={smartChoice_Image}></img>
                  </Flex>
                </Accordion.Content>
              </Accordion.Item>
            </Accordion>
          )}
        </Flex>
        <Markdown>{combinedValue.replace(value[0], '')}</Markdown>
      </Flex>
    );
  };

  return (
    <React.Fragment>
      <Accordion
        css={{
          'abyss-accordion-root': {
            width: '100vw !important',
          },
        }}
        isCollapsible
        onValueChange={setHeaderValues}
        type={'multiple'}
        value={headerValues}
      >
        {content.length &&
          content.map((section) => (
            <AccordionItem
              data-testid="accordion-item"
              key={section?.title}
              value={section?.title}
            >
              <Accordion.Header
                css={accordionHeader}
                data-testid="accordion-header"
              >
                {section?.title}
              </Accordion.Header>
              <Accordion.Content
                css={accordionContent}
                data-testid="accordion-content"
              >
                {section?.key === '3' ? (
                  renderAccordionImages(section)
                ) : (
                  <StyledMarkdown
                    options={{
                      overrides: { Link, Flex },
                    }}
                  >
                    {section?.content ?? ''}
                  </StyledMarkdown>
                )}
                {section?.key === '18' && (
                  <LinkWithTracking
                    analyticsInfo={{
                      location: 'body:accordion-united-health-premium',
                      type: 'pdf',
                    }}
                    css={healthPremiumLinkStyle}
                    data-auto-testid="accordion-united-health-premium-link-text"
                    data-testid="accordion-united-health-premium-link-text"
                    href={navigationURL}
                    openNewWindow
                    size="$lg"
                  >
                    {unitedHealthPremiumLinkText}
                  </LinkWithTracking>
                )}
              </Accordion.Content>
            </AccordionItem>
          ))}
      </Accordion>
    </React.Fragment>
  );
};
